import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import type { ChecklistTemplateItem, ChecklistItemType } from '@/hooks/useChecklists';

interface Props {
  items: ChecklistTemplateItem[];
  onChange: (items: ChecklistTemplateItem[]) => void;
}

let draftIdCounter = 0;
function nextDraftId() {
  return `item-${Date.now()}-${++draftIdCounter}`;
}

const ITEM_TYPES: ChecklistItemType[] = ['checkbox', 'text', 'photo', 'numeric'];

export function ItemEditor({ items, onChange }: Props) {
  const { t } = useTranslation('checklists');
  const { t: tc } = useTranslation('common');

  const handleAdd = () => {
    onChange([
      ...items,
      {
        id: nextDraftId(),
        label: '',
        type: 'checkbox',
        required: false,
        sortOrder: items.length,
      },
    ]);
  };

  const handleRemove = (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    onChange(updated.map((item, i) => ({ ...item, sortOrder: i })));
  };

  const handleChange = (index: number, field: string, value: unknown) => {
    const updated = [...items];
    const item = { ...updated[index]! };

    if (field === 'label' || field === 'type') {
      (item as Record<string, unknown>)[field] = value;
    } else if (field === 'required') {
      item.required = value as boolean;
    } else if (field.startsWith('config.')) {
      const configKey = field.split('.')[1]!;
      item.config = { ...item.config, [configKey]: value };
    } else if (field.startsWith('photoConfig.')) {
      const photoKey = field.split('.')[1]!;
      item.photoConfig = { ...item.photoConfig, [photoKey]: value };
    }

    updated[index] = item;
    onChange(updated);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...items];
    [updated[index - 1], updated[index]] = [updated[index]!, updated[index - 1]!];
    onChange(updated.map((item, i) => ({ ...item, sortOrder: i })));
  };

  const handleMoveDown = (index: number) => {
    if (index >= items.length - 1) return;
    const updated = [...items];
    [updated[index], updated[index + 1]] = [updated[index + 1]!, updated[index]!];
    onChange(updated.map((item, i) => ({ ...item, sortOrder: i })));
  };

  return (
    <Stack spacing={2}>
      {items.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
          {t('template.noItems')}
        </Typography>
      )}

      {items.map((item, index) => (
        <Card key={item.id} variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack spacing={1.5}>
              <Box display="flex" gap={1} alignItems="flex-start">
                <TextField
                  label={t('item.label')}
                  value={item.label}
                  onChange={(e) => handleChange(index, 'label', e.target.value)}
                  size="small"
                  fullWidth
                  required
                />
                <TextField
                  select
                  label={t('item.type')}
                  value={item.type}
                  onChange={(e) => handleChange(index, 'type', e.target.value)}
                  size="small"
                  sx={{ minWidth: 140 }}
                >
                  {ITEM_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(`item.types.${type}`)}
                    </MenuItem>
                  ))}
                </TextField>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.required}
                      onChange={(e) => handleChange(index, 'required', e.target.checked)}
                      size="small"
                    />
                  }
                  label={t('item.required')}
                  sx={{ whiteSpace: 'nowrap' }}
                />
              </Box>

              {/* Type-specific config */}
              {item.type === 'numeric' && (
                <Box display="flex" gap={1}>
                  <TextField
                    label={t('item.config.unit')}
                    value={item.config?.unit ?? ''}
                    onChange={(e) => handleChange(index, 'config.unit', e.target.value)}
                    size="small"
                    sx={{ width: 120 }}
                  />
                  <TextField
                    label={t('item.config.min')}
                    type="number"
                    value={item.config?.min ?? ''}
                    onChange={(e) => handleChange(index, 'config.min', e.target.value ? Number(e.target.value) : undefined)}
                    size="small"
                    sx={{ width: 100 }}
                  />
                  <TextField
                    label={t('item.config.max')}
                    type="number"
                    value={item.config?.max ?? ''}
                    onChange={(e) => handleChange(index, 'config.max', e.target.value ? Number(e.target.value) : undefined)}
                    size="small"
                    sx={{ width: 100 }}
                  />
                </Box>
              )}

              {item.type === 'text' && (
                <TextField
                  label={t('item.config.placeholder')}
                  value={item.config?.placeholder ?? ''}
                  onChange={(e) => handleChange(index, 'config.placeholder', e.target.value)}
                  size="small"
                  fullWidth
                />
              )}

              {/* Photo config — available for all item types */}
              <Box display="flex" gap={2} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.photoConfig?.allowPhoto ?? (item.type === 'photo')}
                      onChange={(e) => handleChange(index, 'photoConfig.allowPhoto', e.target.checked)}
                      size="small"
                    />
                  }
                  label={t('item.photoConfig.allowPhoto')}
                  sx={{ whiteSpace: 'nowrap' }}
                />
                {(item.photoConfig?.allowPhoto || item.type === 'photo') && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={item.photoConfig?.requirePhoto ?? false}
                        onChange={(e) => handleChange(index, 'photoConfig.requirePhoto', e.target.checked)}
                        size="small"
                      />
                    }
                    label={t('item.photoConfig.requirePhoto')}
                    sx={{ whiteSpace: 'nowrap' }}
                  />
                )}
                {(item.photoConfig?.allowPhoto || item.type === 'photo') && (
                  <TextField
                    label={t('item.photoConfig.maxPhotos')}
                    type="number"
                    value={item.photoConfig?.maxPhotos ?? ''}
                    onChange={(e) => handleChange(index, 'photoConfig.maxPhotos', e.target.value ? Number(e.target.value) : undefined)}
                    size="small"
                    sx={{ width: 120 }}
                    inputProps={{ min: 1, max: 10 }}
                  />
                )}
              </Box>

              {/* Actions */}
              <Box display="flex" justifyContent="flex-end" gap={0.5}>
                <Tooltip title={tc('moveUp')}>
                  <span>
                    <IconButton size="small" onClick={() => handleMoveUp(index)} disabled={index === 0} aria-label={tc('moveUp')}>
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={tc('moveDown')}>
                  <span>
                    <IconButton size="small" onClick={() => handleMoveDown(index)} disabled={index === items.length - 1} aria-label={tc('moveDown')}>
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={tc('delete')}>
                  <IconButton size="small" onClick={() => handleRemove(index)} color="error" aria-label={tc('delete')}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ))}

      <Button startIcon={<AddIcon />} onClick={handleAdd} variant="outlined" size="small">
        {t('template.addItem')}
      </Button>
    </Stack>
  );
}
